import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import ahoy from 'ahoy.js';
import Slider from '../shared/Slider';
import IndustryTypeahead from '../shared/IndustryTypeahead';
import useQboPrefill from '../../../hooks/useQboPrefill';

const INDUSTRIES = [
  'Manufacturing', 'Retail', 'Technology', 'Healthcare',
  'Professional Services', 'Construction', 'Hospitality',
  'Food & Beverage', 'Transportation', 'Real Estate',
  'Financial Services', 'Other Services',
];

const SECTOR_MAP = {
  'Manufacturing': 'manufacturing',
  'Retail': 'retail',
  'Technology': 'technology',
  'Healthcare': 'healthcare',
  'Professional Services': 'professional_services',
  'Construction': 'construction',
  'Hospitality': 'food_hospitality',
  'Food & Beverage': 'food_hospitality',
  'Transportation': 'transportation',
  'Real Estate': 'real_estate',
  'Financial Services': 'financial_services',
  'Other Services': 'other',
};

function CurrencyInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-background border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
      />
    </div>
  );
}

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </span>
      {show && (
        <span className="absolute left-6 top-0 z-10 bg-foreground text-background rounded-[8px] p-3 text-xs w-56 shadow-lg">
          {text}
        </span>
      )}
    </span>
  );
}

function formatSnapshotCurrency(value) {
  if (!value && value !== 0) return '$0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000).toLocaleString()}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function QboPrefillBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ml-2"
      style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)', color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.2)' }}
    >
      Pre-filled from QuickBooks
    </span>
  );
}

export default function ValuationStudioWizard({ onSubmit, loading, defaultIndustry, savedSnapshots = [], onLoadSnapshot, qboFinancials = null }) {
  const hasSnapshots = savedSnapshots.length > 0;
  const skipToWizard = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('new') === '1';
  const [step, setStep] = useState(1);
  const [userStartedWizard, setUserStartedWizard] = useState(skipToWizard);

  // Switch to Welcome Back (step 0) when snapshots arrive from async query,
  // but only if the user hasn't already started interacting with the wizard
  // and didn't arrive via ?new=1
  useEffect(() => {
    if (hasSnapshots && step === 1 && !userStartedWizard) {
      setStep(0);
    }
  }, [hasSnapshots]); // only when snapshots arrive, not on every step change

  // Track wizard open
  useEffect(() => {
    ahoy.track('succeedo_valuation_started');
  }, []);

  // Step 1
  const [selectedIndustry, setSelectedIndustry] = useState(
    // Direct match — onboarding and valuation use the same industry names
    INDUSTRIES.find((i) => i === defaultIndustry) || ''
  );

  // Update selectedIndustry when defaultIndustry arrives from async GQL query
  useEffect(() => {
    if (defaultIndustry && !selectedIndustry) {
      const match = INDUSTRIES.find((i) => i === defaultIndustry);
      if (match) setSelectedIndustry(match);
    }
  }, [defaultIndustry]);

  const [selectedNaics, setSelectedNaics] = useState(null); // { naicsCode, benchmarkFamily, industryName, evidenceClass }
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [employees, setEmployees] = useState('');

  // Step 2
  const [revenue, setRevenue] = useState('');
  const [recurringPercent, setRecurringPercent] = useState(50);
  const [growthOption, setGrowthOption] = useState('known');
  const [growthRate, setGrowthRate] = useState('');
  const [year1Revenue, setYear1Revenue] = useState('');
  const [year2Revenue, setYear2Revenue] = useState('');
  const [year3Revenue, setYear3Revenue] = useState('');

  // Step 3
  const [expenses, setExpenses] = useState('');
  const [ownerComp, setOwnerComp] = useState('');
  const [oneTimeExpenses, setOneTimeExpenses] = useState('');
  const [nonOperatingIncome, setNonOperatingIncome] = useState('');
  const [otherAdjustments, setOtherAdjustments] = useState('');
  // SDE-specific inputs. is_owner_operated unlocks the SDE method (5th
  // valuation lens, sized for Main Street businesses where one full-time
  // owner is the centre of the enterprise). owner_total_comp is the
  // FULL annual compensation paid to the owner (salary + benefits +
  // dividends), distinct from owner_excess_comp which is just the
  // above-market portion used for EBITDA recasting.
  const [isOwnerOperated, setIsOwnerOperated] = useState(false);
  const [ownerTotalComp, setOwnerTotalComp] = useState('');

  // QBO pre-fill — extracted into a hook so the wizard stays readable.
  // The hook returns the per-field "prefilled" flags used by the badge UI,
  // plus the QBO-original COGS/OpEx breakdown shown under the combined
  // Operating Expenses field. The legacy `source` object is kept for the
  // in-memory edit-delta UX; persistent provenance is now derived
  // server-side and exposed as `result.data_sources`.
  // `source` is no longer threaded through to the container — provenance is
  // server-derived (todo 125) and surfaced via `result.data_sources`.
  const {
    applied: qboApplied,
    prefilled: qboPrefilled,
    cogs: qboCogs,
    opex: qboOpex,
  } = useQboPrefill(qboFinancials, {
    setRevenue,
    setExpenses,
    setOwnerComp,
    setOneTimeExpenses,
    setYear1Revenue,
    setYear2Revenue,
    setYear3Revenue,
    setGrowthOption,
  });

  // Step 4
  const [tangibleAssets, setTangibleAssets] = useState('');
  const [liabilities, setLiabilities] = useState('');

  const progress = (step / 4) * 100;

  const parseNum = (v) => parseFloat(String(v).replace(/,/g, '')) || 0;

  const calculateCAGR = () => {
    const y1 = parseNum(year1Revenue);
    const y3 = parseNum(year3Revenue);
    if (y1 > 0 && y3 > 0) {
      return ((y3 / y1) ** (1 / 2) - 1) * 100;
    }
    return null;
  };

  const reportedEBITDA = parseNum(revenue) - parseNum(expenses);
  const totalAddBacks = parseNum(ownerComp) + parseNum(oneTimeExpenses) + parseNum(nonOperatingIncome) + parseNum(otherAdjustments);
  const recastedEBITDA = reportedEBITDA + totalAddBacks;

  const handleCalculate = () => {
    const historicalRevenues = growthOption === 'calculate'
      ? [parseNum(year1Revenue), parseNum(year2Revenue), parseNum(year3Revenue)].filter((v) => v > 0)
      : [];

    // Only carry the COGS breakdown forward if the user kept the QBO-prefilled
    // total. If they edited the field, we no longer know what fraction is COGS.
    const expensesUnchangedFromQbo = qboCogs > 0
      && Math.round(parseNum(expenses)) === Math.round(qboCogs + qboOpex);
    const cogsForResult = expensesUnchangedFromQbo ? qboCogs : 0;

    onSubmit({
      revenue: parseNum(revenue),
      operatingExpenses: parseNum(expenses),
      cogs: cogsForResult,
      recurringRevenuePct: recurringPercent / 100,
      adjustments: {
        owner_excess_comp: parseNum(ownerComp),
        one_time_expenses: parseNum(oneTimeExpenses),
        non_operating_income: parseNum(nonOperatingIncome),
        other_adjustments: parseNum(otherAdjustments),
      },
      isOwnerOperated: isOwnerOperated,
      ownerTotalComp: parseNum(ownerTotalComp),
      growthRate: growthOption === 'known' ? parseNum(growthRate) / 100 : null,
      historicalRevenues,
      tangibleAssets: parseNum(tangibleAssets),
      liabilities: parseNum(liabilities),
      industryNaicsCode: selectedNaics?.naicsCode || null,
      sectorCluster: selectedNaics?.sectorCluster || SECTOR_MAP[selectedIndustry] || 'other',
      province: null,
    });
  };

  const stepTitles = ['Industry', 'Revenue', 'Expenses + Recasting', 'Assets'];

  return (
    // Rendered inside AppLayout by the container, as a card in the page like
    // the Deal Structure and Financing tools (not a full-screen takeover).
    <div className="flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-[16px] p-8 max-w-[700px] w-full">

        {/* QBO pre-fill notice — shown once QBO data has been applied */}
        {qboApplied && step > 0 && Object.keys(qboPrefilled).length > 0 && (
          <div
            className="mb-6 flex items-center gap-2 rounded-[10px] px-4 py-3 text-[13px]"
            style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.08)', color: 'hsl(var(--succeedo-orange))' }}
          >
            <img
              src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
              alt="QuickBooks"
              className="w-5 h-5 flex-shrink-0 object-contain"
            />
            <span className="text-foreground/70">Revenue and expense fields have been pre-filled from your QuickBooks data. You can edit any value.</span>
            <a href="/succeedo/integrations" className="ml-auto font-medium hover:underline whitespace-nowrap" style={{ color: 'hsl(var(--succeedo-orange))' }}>Manage</a>
          </div>
        )}

        {/* Progress — hidden on Welcome Back (step 0) */}
        {step > 0 && (
          <div className="mb-8">
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-wide mb-3">
              STEP {step} OF 4 &mdash; {stepTitles[step - 1].toUpperCase()}
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* STEP 0: WELCOME BACK */}
        {step === 0 && (
          <div>
            <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em] mb-2">
              Valuation Studio
            </h1>
            <p className="text-[16px] text-muted-foreground mb-8">
              Pick up where you left off, or start fresh.
            </p>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }} />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  YOUR SAVED VALUATIONS
                </span>
              </div>

              <div className="space-y-3">
                {savedSnapshots.slice(0, 3).map((snap) => {
                  const range = snap.value_range || {};
                  const date = snap.created_at
                    ? new Date(snap.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '';
                  const sector = (snap.sector_cluster || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  const displayValue = snap.ebitda_value != null
                    ? formatSnapshotCurrency(snap.ebitda_value)
                    : `${formatSnapshotCurrency(range.low || 0)} \u2014 ${formatSnapshotCurrency(range.high || 0)}`;

                  return (
                    <button
                      key={snap.id}
                      type="button"
                      onClick={() => onLoadSnapshot && onLoadSnapshot(snap)}
                      className="w-full text-left bg-background border border-input rounded-[12px] p-4 hover:border-[hsl(var(--succeedo-orange))] cursor-pointer transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-[13px] text-muted-foreground">{date}</span>
                          {sector && (
                            <span className="font-mono text-[10px] uppercase border border-border rounded-full px-2 py-0.5 text-muted-foreground">
                              {sector}
                            </span>
                          )}
                          {snap.is_primary && (
                            <span className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                                  style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)', color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.2)' }}>
                              ★ Active
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[15px] font-semibold text-foreground">
                            {displayValue}
                          </span>
                          <span className="text-[13px] font-medium" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                            Open &rarr;
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {savedSnapshots.length > 3 && (
                <button type="button" className="mt-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
                  View all ({savedSnapshots.length})
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-3 font-mono text-[10px] uppercase text-muted-foreground/40">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => { setUserStartedWizard(true); setStep(1); }}
              className="w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors"
            >
              Start New Valuation &rarr;
            </button>
          </div>
        )}

        {/* STEP 1: INDUSTRY */}
        {step === 1 && (
          <div>
            <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em] mb-8">
              Select your industry
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-[13px] text-muted-foreground mb-3">Choose your sector</label>
                <div className="grid grid-cols-2 gap-2">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      type="button"
                      onClick={() => { setSelectedIndustry(ind); setSelectedNaics(null); }}
                      className={`px-4 py-3 rounded-[8px] text-[14px] font-medium transition-all ${
                        selectedIndustry === ind && !selectedNaics
                          ? 'bg-foreground text-background'
                          : 'bg-background border border-input text-foreground hover:border-[hsl(var(--succeedo-orange))]'
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Or search for a specific industry</label>
                <IndustryTypeahead
                  onSelect={(item) => {
                    setSelectedNaics(item);
                    setSelectedIndustry(''); // deselect pill
                  }}
                />
              </div>
              {(selectedNaics || selectedIndustry) && (
                <div className="bg-background border border-border rounded-[8px] p-4">
                  {selectedNaics ? (
                    <p className="text-[13px] text-muted-foreground">
                      Using <span className="font-medium text-foreground">{selectedNaics.benchmarkFamily}</span> benchmarks
                      for <span className="font-medium text-foreground">{selectedNaics.industryName}</span> (NAICS {selectedNaics.naicsCode})
                    </p>
                  ) : (
                    <p className="text-[13px] text-muted-foreground">
                      Using <span className="font-medium text-foreground">{selectedIndustry}</span> benchmarks
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Years in Business</label>
                <input type="number" value={yearsInBusiness} onChange={(e) => setYearsInBusiness(e.target.value)} placeholder="e.g., 15" className="w-full bg-background border border-input rounded-[8px] px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Number of Employees</label>
                <input type="number" value={employees} onChange={(e) => setEmployees(e.target.value)} placeholder="e.g., 25" className="w-full bg-background border border-input rounded-[8px] px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none" />
              </div>
            </div>
            <button type="button" onClick={() => setStep(2)} disabled={!selectedIndustry && !selectedNaics} className="mt-8 w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
              Next: Revenue &rarr;
            </button>
          </div>
        )}

        {/* STEP 2: REVENUE */}
        {step === 2 && (
          <div>
            <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em] mb-8">
              Tell us about your revenue
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">
                  Annual Revenue
                  {qboPrefilled.revenue && <QboPrefillBadge />}
                </label>
                <CurrencyInput value={revenue} onChange={setRevenue} placeholder="2,500,000" />
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-4">
                  What percentage of your revenue is recurring?
                </label>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={recurringPercent}
                  onChange={setRecurringPercent}
                  showValue
                  formatValue={(v) => `${v}%`}
                />
                <div className="flex justify-between mt-2">
                  <span className="text-[12px] text-muted-foreground">0% &mdash; All project/one-time</span>
                  <span className="text-[12px] text-muted-foreground">100% &mdash; All subscription/contract</span>
                </div>
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-3">
                  Growth Rate
                  {qboPrefilled.historicalRevenues && <QboPrefillBadge />}
                </label>
                <div className="flex gap-3 mb-4">
                  <button type="button" onClick={() => setGrowthOption('known')} className={`flex-1 px-4 py-2 rounded-[8px] text-[14px] font-medium transition-all ${growthOption === 'known' ? 'bg-foreground text-background' : 'bg-background border border-input text-foreground'}`}>
                    I know my growth rate
                  </button>
                  <button type="button" onClick={() => setGrowthOption('calculate')} className={`flex-1 px-4 py-2 rounded-[8px] text-[14px] font-medium transition-all ${growthOption === 'calculate' ? 'bg-foreground text-background' : 'bg-background border border-input text-foreground'}`}>
                    Calculate from history
                  </button>
                </div>
                {growthOption === 'known' && (
                  <div className="relative">
                    <input type="number" value={growthRate} onChange={(e) => setGrowthRate(e.target.value)} placeholder="15" className="w-full bg-background border border-input rounded-[8px] px-4 py-3 pr-10 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                )}
                {growthOption === 'calculate' && (
                  <div className="space-y-3">
                    {[['Year 1 Revenue (oldest)', year1Revenue, setYear1Revenue, '2,000,000'],
                      ['Year 2 Revenue', year2Revenue, setYear2Revenue, '2,300,000'],
                      ['Year 3 Revenue (most recent)', year3Revenue, setYear3Revenue, '2,600,000']].map(([label, val, setter, ph]) => (
                      <div key={label}>
                        <label className="block text-[12px] text-muted-foreground mb-1">{label}</label>
                        <CurrencyInput value={val} onChange={setter} placeholder={ph} />
                      </div>
                    ))}
                    {calculateCAGR() !== null && (
                      <div className="bg-background border border-border rounded-[8px] p-3">
                        <p className="text-[13px] text-foreground">
                          Calculated growth rate: <span className="font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>{calculateCAGR().toFixed(1)}%</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 space-y-3">
              <button type="button" onClick={() => setStep(3)} disabled={!revenue} className="w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
                Next: Expenses &rarr;
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-muted-foreground hover:text-foreground transition-colors text-sm">
                &larr; Back
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: EXPENSES + RECASTING */}
        {step === 3 && (
          <div>
            <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em] mb-8">
              Expenses &amp; EBITDA recasting
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">
                  Operating Expenses
                  {qboPrefilled.expenses && <QboPrefillBadge />}
                </label>
                <CurrencyInput value={expenses} onChange={setExpenses} placeholder="2,000,000" />
                <p className="text-[12px] text-muted-foreground/70 mt-2 leading-relaxed">
                  Enter all expenses as reported on your P&amp;L, including your full owner salary.
                  We&apos;ll subtract the above-market portion separately as an add-back below — entering a
                  pre-adjusted number here will double-count it.
                </p>
                {qboPrefilled.expenses && (qboCogs > 0 || qboOpex > 0) && (
                  <p className="mt-2 text-[12px] text-muted-foreground">
                    From QuickBooks:{' '}
                    {qboCogs > 0 && qboOpex > 0
                      ? `${formatSnapshotCurrency(qboCogs)} COGS + ${formatSnapshotCurrency(qboOpex)} Operating Expenses`
                      : qboCogs > 0
                        ? `${formatSnapshotCurrency(qboCogs)} COGS`
                        : `${formatSnapshotCurrency(qboOpex)} Operating Expenses`}
                  </p>
                )}
              </div>

              {/* Owner-operated toggle. When ON, unlocks the SDE valuation
                  method by capturing the owner's full annual compensation
                  separately. The form keeps owner_excess_comp distinct
                  (used for EBITDA recasting); owner_total_comp is the
                  TOTAL salary+benefits+dividends paid to the owner. */}
              <div className="border-t border-border pt-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="text-[14px] font-medium text-foreground mb-1">
                      Is this an owner-operated business?
                    </div>
                    <div className="text-[12px] text-muted-foreground leading-relaxed">
                      Owner-operated means one full-time owner is the centre of the business — a buyer
                      would replace you in the operator&apos;s chair. We&apos;ll add a 5th valuation lens
                      (SDE × Multiple) sized for Main Street businesses.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOwnerOperated((v) => !v)}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mt-1 ${
                      isOwnerOperated ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-border'
                    }`}
                    aria-label="Toggle owner-operated"
                    aria-pressed={isOwnerOperated}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      isOwnerOperated ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                {isOwnerOperated && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-[13px] text-muted-foreground">Owner&apos;s total annual compensation</label>
                      <Tooltip text="Total annual amount the business pays the owner: salary + benefits + dividends/distributions. This is different from 'Owner excess compensation' below — that's only the above-market portion. SDE adds the FULL amount back; EBITDA only adds the excess.">
                        <Info className="w-3.5 h-3.5 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <CurrencyInput value={ownerTotalComp} onChange={setOwnerTotalComp} placeholder="80,000" />
                    <p className="text-[11px] text-muted-foreground/70 mt-2 leading-relaxed">
                      Salary + benefits + any dividends or distributions paid to you. Used by the SDE method.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }} />
                  <span className="font-mono text-[11px] uppercase tracking-wide" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                    RECAST YOUR EBITDA
                  </span>
                </div>
                <p className="text-[13px] text-muted-foreground mb-4">
                  Add back expenses that wouldn&apos;t continue under new ownership
                </p>
                <div className="space-y-3">
                  {[
                    ['Owner excess compensation', ownerComp, setOwnerComp, 'Amount you pay yourself above market rate for your role', qboPrefilled.ownerComp],
                    ['One-time expenses', oneTimeExpenses, setOneTimeExpenses, 'Non-recurring costs like legal fees, equipment repairs', qboPrefilled.oneTimeExpenses],
                    ['Non-operating income', nonOperatingIncome, setNonOperatingIncome, 'Income not related to core business operations', false],
                    ['Other adjustments', otherAdjustments, setOtherAdjustments, 'Any other non-recurring or owner-specific expenses', false],
                  ].map(([label, val, setter, tip, prefilled]) => (
                    <div key={label}>
                      <div className="flex items-center gap-2 mb-1">
                        <label className="text-[13px] text-muted-foreground">
                          {label}
                          {prefilled && <QboPrefillBadge />}
                        </label>
                        <Tooltip text={tip}>
                          <Info className="w-3.5 h-3.5 text-muted-foreground" />
                        </Tooltip>
                      </div>
                      <CurrencyInput value={val} onChange={setter} placeholder="0" />
                    </div>
                  ))}
                </div>
                {(reportedEBITDA !== 0 || totalAddBacks > 0) && (
                  <div className="mt-4 bg-background border border-border rounded-[8px] p-4 flex items-center gap-3 flex-wrap">
                    <span className="text-[13px] text-muted-foreground">Reported EBITDA:</span>
                    <span className="text-[15px] font-semibold text-foreground">${reportedEBITDA.toLocaleString()}</span>
                    <span style={{ color: 'hsl(var(--succeedo-orange))' }}>&rarr;</span>
                    <span className="text-[13px] text-muted-foreground">Recasted EBITDA:</span>
                    <span className="text-[15px] font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>${recastedEBITDA.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 space-y-3">
              <button type="button" onClick={() => setStep(4)} disabled={!expenses} className="w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
                Next: Assets (Optional) &rarr;
              </button>
              <button type="button" onClick={() => setStep(2)} className="w-full text-muted-foreground hover:text-foreground transition-colors text-sm">
                &larr; Back
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: ASSETS */}
        {step === 4 && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em]">
                Assets &amp; liabilities
              </h1>
              <span className="font-mono text-[10px] uppercase text-muted-foreground bg-background border border-border rounded-full px-3 py-1">
                OPTIONAL
              </span>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Tangible Assets</label>
                <CurrencyInput value={tangibleAssets} onChange={setTangibleAssets} placeholder="500,000" />
                <p className="text-[12px] text-muted-foreground mt-1">Equipment, inventory, real estate</p>
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Liabilities</label>
                <CurrencyInput value={liabilities} onChange={setLiabilities} placeholder="200,000" />
                <p className="text-[12px] text-muted-foreground mt-1">Loans, payables, debt</p>
              </div>
            </div>
            <div className="mt-8 space-y-3">
              <button type="button" onClick={handleCalculate} disabled={loading} className="w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
                {loading ? 'Calculating\u2026' : 'Calculate My Valuation \u2192'}
              </button>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(3)} className="flex-1 text-muted-foreground hover:text-foreground transition-colors text-sm">
                  &larr; Back
                </button>
                <button type="button" onClick={handleCalculate} disabled={loading} className="flex-1 text-muted-foreground hover:text-foreground transition-colors text-sm">
                  Skip this step &rarr;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
